// Mirrored in the cobrowse-relay repo (src/protocol.ts), which also hosts the sales console: change both together.

// Bump on any breaking change to commands, client messages, the recording frames or the rrweb version,
// together with the relay's: the console then asks the sales person to have the client reload.
export const COBROWSE_PROTOCOL_VERSION = 1;

export const CobrowseRole = {
    Agent: 'agent',
    Client: 'client',
} as const;

export type CobrowseRole = (typeof CobrowseRole)[keyof typeof CobrowseRole];

// Ratios are 0..1 inside the node's bounding box, so a target survives scroll and viewport differences.
export interface NodeTarget {
    nodeId: number;
    ratioX: number;
    ratioY: number;
}

export interface KeyModifiers {
    shiftKey: boolean;
    altKey: boolean;
    ctrlKey: boolean;
    metaKey: boolean;
}

// Same semantics as a DOM Range boundary: a character offset in a text node, a child index in an element.
export interface TextPosition {
    nodeId: number;
    offset: number;
}

export type AgentCommand =
    | { type: 'pointer:move'; target: NodeTarget }
    | { type: 'pointer:hide' }
    | { type: 'click'; target: NodeTarget }
    | { type: 'double-click'; target: NodeTarget }
    | { type: 'selection:set'; anchor: TextPosition; focus: TextPosition }
    | { type: 'text:insert'; text: string }
    | ({ type: 'key:press'; key: string; code: string } & KeyModifiers)
    | { type: 'scroll'; nodeId: number | null; deltaX: number; deltaY: number }
    | { type: 'select:change'; nodeId: number; value: string }
    | { type: 'field:set-value'; nodeId: number; value: string }
    | { type: 'select:open'; nodeId: number }
    | { type: 'select:highlight'; value: string | null }
    | { type: 'select:close' }
    | { type: 'snapshot:request' }
    | { type: 'session:end' };

export type CommandRejectionReason = 'node-not-found' | 'blocked' | 'unsupported';

export interface FieldSelection {
    nodeId: number;
    start: number;
    end: number;
}

export type ClientMessage =
    | { type: 'client:hello'; protocolVersion: number }
    | { type: 'client:heartbeat' }
    | { type: 'command:rejected'; command: AgentCommand['type']; reason: CommandRejectionReason }
    | { type: 'field:selection'; selection: FieldSelection | null }
    | { type: 'session:end' };

export type SessionEndReason = 'ended-by-agent' | 'ended-by-client' | 'declined-by-client' | 'expired';

export type RelayMessage = { type: 'peer:joined'; role: CobrowseRole } | { type: 'peer:left'; role: CobrowseRole } | { type: 'session:ended'; reason: SessionEndReason };

export interface CreateSessionRequest {
    tenantId: string;
    userId: string;
    websocketChannel: string;
    agent: { id: string; name: string };
}

export const CobrowsePushEvent = {
    Requested: 'cobrowse:session:requested',
    Answered: 'cobrowse:session:answered',
} as const;

export interface SessionRequestedPayload {
    sessionId: string;
    userId: string;
    agentName: string;
    expiresAt: number;
}

export interface PendingSessionResponse {
    request: SessionRequestedPayload | null;
}

export interface SessionAnsweredPayload {
    sessionId: string;
}

export interface CreateSessionResponse {
    sessionId: string;
    agentToken: string;
    consoleUrl: string;
    expiresAt: number;
}

export interface JoinSessionResponse {
    clientToken: string;
    agentName: string;
    expiresAt: number;
}

export const SELECT_LIST_MAX_HEIGHT_PX = 256;
export const SELECT_OPTION_HEIGHT_PX = 36;

export function estimateSelectListHeight(optionCount: number): number {
    return Math.min(SELECT_LIST_MAX_HEIGHT_PX, optionCount * SELECT_OPTION_HEIGHT_PX + 8);
}

// Both browsers send KEEPALIVE_PING on their socket; the relay runtime answers KEEPALIVE_PONG without
// waking the session. A socket left silent is dropped by the network without either side being told,
// and a missing pong is how each side notices it must reconnect.
export const KEEPALIVE_PING = 'ping';
export const KEEPALIVE_PONG = 'pong';
export const KEEPALIVE_INTERVAL_MS = 10_000;
export const KEEPALIVE_TIMEOUT_MS = 6_000;

// The console tells a frozen client (locked phone, app in the background) from a quiet one by these.
export const CLIENT_HEARTBEAT_INTERVAL_MS = 10_000;

export const COBROWSE_SESSION_TTL_MS = 2 * 60 * 60 * 1000;

export function parseJsonMessage<T extends { type: string }>(raw: string): T | null {
    try {
        const message: unknown = JSON.parse(raw);

        if (typeof message !== 'object' || message === null || typeof (message as { type?: unknown }).type !== 'string') {
            return null;
        }

        return message as T;
    } catch {
        return null;
    }
}
